import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, Mail, Phone, MapPin, Youtube } from "lucide-react";

// Vimeo icon inline
function VimeoIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.409 0-2.578-1.294-3.553-3.881l-1.936-7.101c-.719-2.584-1.491-3.878-2.324-3.878-.18 0-.824.377-1.925 1.121L0 7.16c1.212-1.064 2.407-2.128 3.587-3.191 1.628-1.406 2.85-2.146 3.667-2.221 1.928-.184 3.115 1.132 3.559 3.945.479 3.036.812 4.923.998 5.66.559 2.541 1.169 3.809 1.837 3.809.517 0 1.294-.816 2.336-2.45 1.037-1.634 1.596-2.878 1.663-3.732.14-1.34-.396-2.008-1.601-2.008-.564 0-1.145.132-1.744.377 1.157-3.794 3.368-5.635 6.635-5.528 2.421.075 3.566 1.665 3.435 4.762z" />
    </svg>
  );
}

function CircleIconLink({ href, label, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-[#ff8a3c] transition-colors"
    >
      {children}
    </a>
  );
}

function UniversoBlock({ dot, label, links, accent }) {
  return (
    <div>
      <div className="flex items-center gap-3">
        <span className="w-2 h-2 rounded-full" style={{ background: dot }} />
        <Link to={label === "KILOMBO MUSIC" ? "/music" : "/teams"} className="font-heading text-lg md:text-xl tracking-[0.08em] text-white hover:opacity-90 transition-opacity">
          {label}
        </Link>
      </div>
      <div className="mt-4 flex items-center gap-3">
        {links.map((l, i) => (
          <a
            key={i}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${label} · ${l.label}`}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-colors border"
            style={{ borderColor: `${accent}55`, color: accent }}
            onMouseEnter={(e) => (e.currentTarget.style.background = `${accent}18`)}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            {l.icon}
          </a>
        ))}
      </div>
    </div>
  );
}

export default function Footer({ footer }) {
  const KILOMBO_LINKS = {
    linkedin: "https://www.linkedin.com/company/kilombo-estudio-de-contenidos/",
    instagram: "https://www.instagram.com/kilombo_estudio",
    facebook: "https://www.facebook.com/kilomboestudio",
  };
  const MUSIC_LINKS = [
    { label: "Instagram", href: "https://www.instagram.com/kilomboestudiomusic/", icon: <Instagram className="w-4 h-4" /> },
    { label: "LinkedIn", href: "https://www.linkedin.com/in/jairo-vargas-kilombo/", icon: <Linkedin className="w-4 h-4" /> },
  ];
  const TEAMS_LINKS = [
    { label: "Instagram", href: "https://www.instagram.com/kilomboteams/", icon: <Instagram className="w-4 h-4" /> },
    { label: "LinkedIn", href: "https://www.linkedin.com/in/alejandromorales-kilomboestudio/", icon: <Linkedin className="w-4 h-4" /> },
  ];

  return (
    <footer className="relative bg-black pt-14 md:pt-16 pb-8 px-6">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
        {/* COLUMNA 1 — Marca + redes generales */}
        <div>
          <div className="flex items-start gap-3">
            <img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_team-workspace-60/artifacts/3ti1wpm1_color-clanco-apaisado.webp"
              alt="Kilombo"
              className="h-10 md:h-12 w-auto object-contain"
              loading="lazy"
              decoding="async"
              data-testid="footer-logo"
            />
          </div>
          <div className="mt-3 font-mono-caps text-xs tracking-[0.3em] text-white/50 uppercase">
            {footer.tagline}
          </div>
          <p className="mt-6 text-white/60 text-sm leading-relaxed max-w-sm">{footer.description}</p>
          <p className="mt-6 font-mono-caps text-[10px] tracking-[0.3em] uppercase text-white/40">Del estudio</p>
          <div className="mt-3 flex items-center gap-3 flex-wrap" data-testid="footer-kilombo-socials">
            <CircleIconLink href={KILOMBO_LINKS.linkedin} label="Kilombo · LinkedIn"><Linkedin className="w-4 h-4" /></CircleIconLink>
            <CircleIconLink href={KILOMBO_LINKS.instagram} label="Kilombo · Instagram"><Instagram className="w-4 h-4" /></CircleIconLink>
            <CircleIconLink href={KILOMBO_LINKS.facebook} label="Kilombo · Facebook"><Facebook className="w-4 h-4" /></CircleIconLink>
          </div>
        </div>

        {/* COLUMNA 2 — Universos */}
        <div>
          <h4 className="font-mono-caps text-xs tracking-[0.3em] text-white/50 uppercase mb-5 md:mb-6">Universos</h4>
          <div className="space-y-7 md:space-y-8">
            <UniversoBlock
              dot="#ff6a2b"
              accent="#ff8a3c"
              label="KILOMBO MUSIC"
              links={MUSIC_LINKS}
            />
            <UniversoBlock
              dot="#2ec9a8"
              accent="#2ec9a8"
              label="KILOMBO TEAMS"
              links={TEAMS_LINKS}
            />
          </div>
        </div>

        {/* COLUMNA 3 — Contacto */}
        <div>
          <h4 className="font-mono-caps text-xs tracking-[0.3em] text-white/50 uppercase mb-5 md:mb-6">Contacto</h4>
          <ul className="space-y-3 md:space-y-4 text-white/80 text-sm">
            <li className="flex items-center gap-3 break-all"><Mail className="w-4 h-4 text-white/50 shrink-0" /> {footer.email}</li>
            <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-white/50 shrink-0" /> {footer.phone}</li>
            <li className="flex items-center gap-3"><MapPin className="w-4 h-4 text-white/50 shrink-0" /> {footer.location}</li>
          </ul>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mt-12 md:mt-16 pt-6 border-t border-white/5 flex items-center justify-center text-xs text-white/40">
        <p className="text-center">{footer.copyright}</p>
      </div>
    </footer>
  );
}
